import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { TemplateBasicInfo } from "./TemplateBasicInfo";
import { TemplateVariables } from "./TemplateVariables";
import { PREDEFINED_VARIABLES } from "../constants";
import { toast } from "sonner";
import { useTemplateCreation } from "../hooks/useTemplateCreation";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TemplatePrivacySettings } from "../editor/TemplatePrivacySettings";

interface TemplateFormProps {
  onCancel: () => void;
}

export const TemplateForm = ({ onCancel }: TemplateFormProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [templateType, setTemplateType] = useState<string>("");
  const [targetAudience, setTargetAudience] = useState<string>("all");
  const [content, setContent] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<keyof typeof PREDEFINED_VARIABLES>("USER");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [includePrivacyNotice, setIncludePrivacyNotice] = useState(false);
  const [privacyNoticePosition, setPrivacyNoticePosition] = useState<string>("footer");
  const [customPrivacyText, setCustomPrivacyText] = useState("");
  
  const { createTemplateMutation } = useTemplateCreation();

  const handleInsertVariable = (variable: string) => {
    const variableText = `{${variable}}`;
    setContent((prev) => prev + variableText);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await createTemplateMutation.mutateAsync({
        name,
        description,
        template_type: templateType,
        content_structure: {
          template: content,
          variables: {},
          sections: [{
            name: 'main',
            type: 'content',
            content: content
          }]
        },
        user_category: targetAudience,
        include_privacy_notice: includePrivacyNotice,
        privacy_notice_position: privacyNoticePosition,
        custom_privacy_text: customPrivacyText,
      });
      handleCancel();
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = () => {
    if (!name.trim()) {
      toast.error("Template name is required");
      return false;
    }
    if (!templateType) {
      toast.error("Template type is required");
      return false;
    }
    if (!content.trim()) {
      toast.error("Template content is required");
      return false;
    }
    return true;
  };

  const handleCancel = () => {
    setName("");
    setDescription("");
    setTemplateType("");
    setTargetAudience("all");
    setContent("");
    setSelectedCategory("USER");
    setIncludePrivacyNotice(false);
    setPrivacyNoticePosition("footer");
    setCustomPrivacyText("");
    onCancel();
  };

  return (
    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-6">
      <Tabs defaultValue="content" className="w-full">
        <TabsList>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="variables">Variables</TabsTrigger>
          <TabsTrigger value="privacy">Privacy</TabsTrigger>
        </TabsList>

        <TabsContent value="content">
          <div className="space-y-4">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full h-64 p-4 border rounded-md"
              placeholder="Enter template content..."
            />
          </div>
        </TabsContent>

        <TabsContent value="settings">
          <TemplateBasicInfo
            name={name}
            setName={setName}
            description={description}
            setDescription={setDescription}
            templateType={templateType}
            setTemplateType={setTemplateType}
            targetAudience={targetAudience}
            setTargetAudience={setTargetAudience}
          />
        </TabsContent>

        <TabsContent value="variables">
          <TemplateVariables
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            content={content}
            setContent={setContent}
            handleInsertVariable={handleInsertVariable}
          />
        </TabsContent>

        <TabsContent value="privacy">
          <TemplatePrivacySettings
            includePrivacyNotice={includePrivacyNotice}
            privacyNoticePosition={privacyNoticePosition}
            customPrivacyText={customPrivacyText}
            defaultPrivacyText="Data Collection and Usage: We collect and process your personal information to deliver our newsletter and improve your experience. Your data is handled in accordance with our Privacy Policy.

Unsubscribe: You can unsubscribe at any time using the link at the bottom of our emails.

For privacy concerns, contact: privacy@example.com"
            onIncludePrivacyNoticeChange={setIncludePrivacyNotice}
            onPrivacyNoticePositionChange={setPrivacyNoticePosition}
            onCustomPrivacyTextChange={setCustomPrivacyText}
          />
        </TabsContent>
      </Tabs>

      <div className="flex justify-end space-x-2 sticky bottom-0 pt-4 bg-background border-t">
        <Button 
          type="button" 
          variant="outline" 
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            'Create Template'
          )}
        </Button>
      </div>
    </form>
  );
};